import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { fpColors } from '../../../styles/theme';
import type { GroupSummaryForItem } from '../../../api/types';
import { iconForKind } from '../../sharing/groupKindMeta';

/**
 * FE-207 — ItemDetail "Shared in groups" card.
 *
 * Read-only display of the groups this item is bound to. Tap a row
 * to navigate to GroupDetail. The card is owner-only — non-owners
 * (custodians, borrowers) get no value from seeing the binding list
 * AND would mostly resolve to "you can't see this group anyway".
 *
 * The PERSONAL kind is always filtered out — it's the hidden
 * per-user bucket every item lives in by default. Showing it adds
 * noise without information.
 *
 * The "+ Add to another group" CTA at the bottom is wired in
 * **FE-208**. This component scopes ONLY to display + the entry
 * point click; the picker sheet lives there. For now it accepts an
 * `onAddPress` callback that the parent may or may not provide.
 */

interface Props {
  groups: GroupSummaryForItem[] | undefined;
  viewerIsOwner: boolean;
  /** Optional callback for "+ Add to another group". When omitted,
   *  the CTA is hidden. Lets us ship FE-207 without FE-208 wired up. */
  onAddPress?: () => void;
}

export const ItemSharedInGroupsCard: React.FC<Props> = ({
  groups,
  viewerIsOwner,
  onAddPress,
}) => {
  const navigation = useNavigation<any>();

  // Owner-only surface.
  if (!viewerIsOwner) return null;

  // Strip PERSONAL — never user-facing.
  const visible = (groups ?? []).filter(
    (g) => g.kind !== 'PERSONAL',
  );

  // Card hidden entirely when item isn't shared anywhere AND there's
  // no Add-CTA wired. Once FE-208 is wired we can keep the card
  // visible with just the Add button.
  if (visible.length === 0 && !onAddPress) return null;

  return (
    <View style={styles.card} testID="item-shared-in-groups-card">
      <View style={styles.header}>
        <MaterialIcons
          name="groups"
          size={18}
          color={fpColors.primary}
        />
        <Text style={styles.title}>Shared in circles</Text>
        {visible.length > 0 ? (
          <View style={styles.countPill}>
            <Text style={styles.countPillText}>{visible.length}</Text>
          </View>
        ) : null}
      </View>

      {visible.length === 0 ? (
        <Text style={styles.empty}>
          Not in any circle yet. Add this item to a circle so others can
          see + borrow it.
        </Text>
      ) : (
        <View style={styles.list}>
          {visible.map((g) => (
            <Pressable
              key={g.id}
              onPress={() =>
                navigation.navigate('GroupDetail', {
                  groupId: String(g.id),
                })
              }
              style={({ pressed }) => [
                styles.row,
                pressed && { opacity: 0.85 },
              ]}
              accessibilityRole="button"
              accessibilityLabel={`Open ${g.name}`}
              testID={`item-shared-in-groups-row-${g.id}`}
            >
              <View style={styles.iconWrap}>
                <MaterialIcons
                  name={iconForKind(g.kind)}
                  size={18}
                  color={fpColors.primary}
                />
              </View>
              <Text style={styles.rowName} numberOfLines={1}>
                {g.name}
              </Text>
              <MaterialIcons
                name="chevron-right"
                size={20}
                color={fpColors.outline}
              />
            </Pressable>
          ))}
        </View>
      )}

      {onAddPress ? (
        <Pressable
          onPress={onAddPress}
          style={({ pressed }) => [
            styles.addBtn,
            pressed && { opacity: 0.85 },
          ]}
          accessibilityRole="button"
          accessibilityLabel="Add this item to another circle"
          testID="item-shared-in-groups-add-btn"
        >
          <MaterialIcons
            name="add"
            size={16}
            color={fpColors.primary}
          />
          <Text style={styles.addBtnText}>
            {visible.length === 0 ? 'Share with a circle' : 'Manage sharing'}
          </Text>
        </Pressable>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    padding: 14,
    borderRadius: 14,
    backgroundColor: fpColors.surfaceContainerLow,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    gap: 10,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  title: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
    flex: 1,
  },
  countPill: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 999,
    backgroundColor: fpColors.primaryContainer,
  },
  countPillText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 11,
    color: fpColors.onPrimaryContainer,
  },
  empty: {
    fontFamily: 'Inter_400Regular',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
    lineHeight: 17,
  },
  list: { gap: 6 },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 10,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: fpColors.surface,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
  },
  iconWrap: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: fpColors.primaryContainer,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowName: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 13,
    color: fpColors.onSurface,
    flex: 1,
  },
  addBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 10,
    borderRadius: 10,
    backgroundColor: fpColors.primaryContainer,
  },
  addBtnText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 13,
    color: fpColors.primary,
  },
});

export default ItemSharedInGroupsCard;
